import React from 'react';
import { Mail, Calendar, Link2, AlertCircle } from 'lucide-react';
import { Email } from '../types/email';
import { ReferenceTag } from './ReferenceTag';
import { RecordCard } from './RecordCard';
interface EmailDetailViewProps {
  email: Email;
  onManualLink: () => void;
  onUnlink: (recordId: string) => void;
}
export function EmailDetailView({
  email,
  onManualLink,
  onUnlink
}: EmailDetailViewProps) {
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    }).format(date);
  };
  const highlightReferences = (text: string) => {
    let highlighted = text;
    email.references.forEach(ref => {
      const regex = new RegExp(`(${ref.number})`, 'g');
      highlighted = highlighted.replace(regex, `<mark class="bg-yellow-200 px-1 rounded font-medium">$1</mark>`);
    });
    return highlighted;
  };
  return <div className="h-full flex flex-col bg-white" data-id="element-12">
      {/* Teal Header */}
      <div className="px-4 py-3" style={{
      backgroundColor: 'var(--teal-section)'
    }} data-id="element-13">
        <h2 className="text-white font-medium" data-id="element-14">Email Details</h2>
      </div>

      <div className="flex-1 overflow-y-auto" data-id="element-15">
        {/* Email Info */}
        <div className="p-4 border-b" style={{
        borderColor: 'var(--border-light)'
      }} data-id="element-16">
          <div className="space-y-2 text-sm" data-id="element-17">
            <div className="flex gap-2" data-id="element-18">
              <span className="text-gray-500 w-20" data-id="element-19">From:</span>
              <span className="text-gray-900 font-medium" data-id="element-20">{email.from}</span>
            </div>
            <div className="flex gap-2" data-id="element-21">
              <span className="text-gray-500 w-20" data-id="element-22">Subject:</span>
              <span className="text-gray-900 font-medium" data-id="element-23">{email.subject}</span>
            </div>
            <div className="flex gap-2" data-id="element-24">
              <span className="text-gray-500 w-20" data-id="element-25">Date:</span>
              <span className="text-gray-700" data-id="element-26">
                {formatDate(email.receivedAt)}
              </span>
            </div>
          </div>

          {email.references.length > 0 && <div className="mt-4 pt-4 border-t" style={{
          borderColor: 'var(--border-light)'
        }} data-id="element-27">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-2" data-id="element-28">
                Detected References:
              </span>
              <div className="flex items-center gap-2 flex-wrap" data-id="element-29">
                {email.references.map(ref => <ReferenceTag key={ref.id} reference={ref} data-id="element-30" />)}
              </div>
            </div>}
        </div>

        {/* Email Body */}
        <div className="p-4 border-b" style={{
        borderColor: 'var(--border-light)'
      }} data-id="element-31">
          <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed" dangerouslySetInnerHTML={{
          __html: highlightReferences(email.body)
        }} data-id="element-32" />
        </div>
      </div>

      {/* Linked Records Section */}
      <div className="border-t p-4" style={{
      borderColor: 'var(--border-light)',
      backgroundColor: '#fafbfc'
    }} data-id="element-33">
        <div className="flex items-center justify-between mb-3" data-id="element-34">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-35">
            <Link2 className="w-4 h-4" data-id="element-36" />
            Linked Records
          </h3>
          <button onClick={onManualLink} className="px-4 py-2 text-white text-sm font-medium rounded hover:opacity-90 transition-opacity" style={{
          backgroundColor: 'var(--teal-section)'
        }} data-id="element-37">
            Link Manually
          </button>
        </div>

        {email.linkedRecords.length > 0 ? <div className="space-y-2" data-id="element-38">
            {email.linkedRecords.map(record => <RecordCard key={record.id} record={record} onUnlink={() => onUnlink(record.id)} data-id="element-39" />)}
          </div> : <div className="flex items-center gap-3 p-3 bg-orange-50 border rounded" style={{
        borderColor: 'var(--orange-status)'
      }} data-id="element-40">
            <AlertCircle className="w-5 h-5 flex-shrink-0" style={{
          color: 'var(--orange-status)'
        }} data-id="element-41" />
            <div data-id="element-42">
              <p className="text-sm font-medium text-gray-900" data-id="element-43">
                No linked records
              </p>
              <p className="text-xs text-gray-600 mt-0.5" data-id="element-44">
                {email.references.length === 0 ? 'No reference numbers found in this email' : 'Reference numbers found but not matched in system'}
              </p>
            </div>
          </div>}
      </div>
    </div>;
}